import { useState, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./calculator.module.scss";
import UnitSelector from "./CalculatorUnitSelector";

const CalculatorInput = ({
  label,
  id,
  value,
  onChange,
  error,
  unitOptions = [],
  unit,
  unitValue,
  onUnitChange,
  onUnitClick,
  inputProps,
  isWebLayout = false,
  ...rest
}) => {
  const [showUnitSelector, setShowUnitSelector] = useState(false);
  const { forceDropdown, ...domRest } = rest;

  const handleUnitSelect = (selectedUnit) => {
    if (onUnitChange) {
      const syntheticEvent = {
        target: { value: selectedUnit },
      };
      onUnitChange(syntheticEvent);
    }
  };

  const handleUnitClick = () => {
    if (unitOptions && unitOptions.length > 1) {
      setShowUnitSelector(true);
    } else if (onUnitClick) {
      onUnitClick();
    }
  };

  // helper function to get display unit for button
  const getDisplayUnit = () => {
    if (unit) return unit;
    if (unitOptions && unitOptions.length > 0 && unitValue) {
      const selectedOption = unitOptions.find((opt) => opt.value === unitValue);
      return selectedOption?.shortLabel || selectedOption?.label || unitValue;
    }
    if (unitOptions && unitOptions.length === 1) {
      return unitOptions[0].shortLabel || unitOptions[0].label;
    }
    return null;
  };

  const inputRef = useRef(null);

  const handleFocus = (e) => {
    if (inputProps?.onFocus) {
      inputProps.onFocus(e);
    }
    // Scroll the input into view for mobile keyboards
    setTimeout(() => {
      if (e.target && typeof e.target.scrollIntoView === "function") {
        e.target.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 100);
  };

  return (
    <div className={styles.formField}>
      <label className={styles.fieldLabel} htmlFor={id}>
        {label}
      </label>
      <div className={styles.inputRow}>
        <div
          className={
            styles.inputContainer +
            (error ? " " + styles.inputContainerError : "") +
            (isWebLayout ? " " + styles.inputContainerRelative : "")
          }
        >
          <input
            className={styles.input}
            id={id}
            value={value}
            onChange={onChange}
            onFocus={handleFocus}
            ref={inputRef}
            inputMode="decimal"
            {...inputProps}
            {...domRest}
          />

          {(unitOptions && unitOptions.length > 1) || (unit && onUnitClick) ? (
            <button
              type="button"
              className={styles.unitSelector}
              onClick={unitOptions && unitOptions.length > 1 ? handleUnitClick : onUnitClick}
              aria-label={`Change unit for ${label}`}
            >
              <span className={styles.unitDropdownText}>{getDisplayUnit()}</span>
              <span
                className={`${styles.unitDropdownArrow} ${showUnitSelector ? styles.unitDropdownArrowOpen : ""}`}
              >
                <i className="tg-icons-ChevronDown"></i>
              </span>
            </button>
          ) : (
            // static unit label
            <span className={styles.unit}>{getDisplayUnit()}</span>
          )}

          {/* Render dropdown inside container for proper positioning */}
          {unitOptions && unitOptions.length > 1 && (
            <UnitSelector
              visible={showUnitSelector}
              units={unitOptions}
              selected={unitValue}
              onSelect={handleUnitSelect}
              onClose={() => setShowUnitSelector(false)}
              isWebLayout={isWebLayout}
              {...rest}
            />
          )}
        </div>
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

CalculatorInput.propTypes = {
  label: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  unitOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string.isRequired,
      shortLabel: PropTypes.string,
    }),
  ),
  unit: PropTypes.string,
  unitValue: PropTypes.string,
  onUnitChange: PropTypes.func,
  onUnitClick: PropTypes.func,
  inputProps: PropTypes.object,
  isWebLayout: PropTypes.bool,
};

export default CalculatorInput;
